import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { useToast } from '../components/common/ToastContainer';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; requiresPasswordChange?: boolean; error?: string; redirectTo?: string }>;
  logout: () => void;
  changePassword: (newPassword: string) => Promise<{ success: boolean; error?: string; redirectTo?: string }>;
  refreshToken: () => Promise<string | null>;
  getValidToken: () => Promise<string | null>;
}

const COGNITO_CLIENT_ID = '2on4e61mjbiqc4if6ua04g9cad';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to check if token is expired (with 5 min buffer)
const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 5 * 60; // 5 minutes buffer
    
    return payload.exp < (currentTime + bufferTime);
  } catch {
    return true;
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const toast = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('=== BACKEND AUTH LOGIN ===');
      console.log('Email:', email);
      
      // ✅ USE BACKEND AUTH ENDPOINT (not direct Cognito)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      console.log('Backend auth response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Backend auth response data:', data);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        return { success: false, error: 'Invalid response from server - not valid JSON' };
      }

      // Handle password change required (can come with 200 or non-200 status)
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('Password change required');
        localStorage.setItem('tempSession', JSON.stringify({
          email,
          session: data.session
        }));
        return { success: true, requiresPasswordChange: true };
      }

      if (!response.ok) {
        console.error('Backend auth failed with status:', response.status, 'Data:', data);
        const errorMsg = data.message || data.error || `Authentication failed: ${response.status}`;
        toast.error('Login failed', errorMsg);
        return { success: false, error: errorMsg };
      }

      // Extract tokens and user data from backend response
      const accessToken = data.tokens?.accessToken;
      const idToken = data.tokens?.idToken;
      const refreshToken = data.tokens?.refreshToken;
      const user = data.user;
      
      if (!accessToken) {
        console.error('No access token in backend response:', data);
        const errorMsg = 'No access token received from authentication service';
        toast.error('Login failed', errorMsg);
        return { success: false, error: errorMsg };
      }

      if (!user) {
        console.error('No user data in backend response:', data);
        const errorMsg = 'No user data received from authentication service';
        toast.error('Login failed', errorMsg);
        return { success: false, error: errorMsg };
      }

      console.log('✅ Backend auth successful');
      console.log('✅ User data from backend:', user);
      console.log('✅ Cognito ID:', user.id);
      console.log('✅ Database healthProfessionalId:', user.healthProfessionalId);
      console.log('✅ Doctor name:', user.name);
      console.log('✅ Doctor email:', user.email);
      console.log('✅ Doctor timezone:', user.timezone);

      // Create user object from backend response
      const userData: User = {
        id: user.id,  // Cognito ID (for auth only)
        email: user.email,
        role: user.role as 'admin' | 'doctor',
        name: user.name,
        healthProfessionalId: user.healthProfessionalId  // ✅ Database ID (for API calls)
      };

      console.log('✅ Final user data:', userData);
      console.log('✅ healthProfessionalId that will be used for API calls:', userData.healthProfessionalId);

      // ✅ Store doctor's timezone if provided
      if (user.timezone && user.role === 'doctor') {
        console.log('✅ Storing doctor timezone:', user.timezone);
        const cacheData = {
          timezone: user.timezone,
          timestamp: Date.now(),
          expiresIn: 90 * 24 * 60 * 60 * 1000 // 90 days
        };
        localStorage.setItem('doctor_timezone_cache', JSON.stringify(cacheData));
      }

      // Clear any old data first before setting new user
      localStorage.clear();
      sessionStorage.clear();

      setToken(accessToken);
      setUser(userData);
      
      // Store fresh data in localStorage
      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('authUser', JSON.stringify(userData));
      localStorage.setItem('userRole', user.role);
      if (idToken) {
        localStorage.setItem('idToken', idToken);
      }
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      
      // Determine redirect path based on user role
      const redirectTo = user.role === 'admin' ? '/admin' : '/doctor';
      
      toast.success('Login successful', `Welcome back, ${user.name}!`);
      return { success: true, redirectTo };

    } catch (error) {
      console.error('Backend auth error:', error);
      let errorMsg = 'Authentication service error. Please try again.';
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMsg = 'Network error. Please check your connection and try again.';
      }
      toast.error('Login failed', errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const changePassword = async (newPassword: string) => {
    try {
      const tempSession = localStorage.getItem('tempSession');
      if (!tempSession) {
        return { success: false, error: 'No active session for password change' };
      }

      const { email, session } = JSON.parse(tempSession);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';

      console.log('Changing password for:', email);

      const response = await fetch(`${apiUrl}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          newPassword,
          session
        }),
      });

      console.log('Password change response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Password change response data:', data);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        return { success: false, error: 'Invalid response from server - not valid JSON' };
      }

      if (!response.ok) {
        console.error('Password change failed with status:', response.status, 'Data:', data);
        const errorMsg = data.message || data.error || `Server error: ${response.status}`;
        toast.error('Password change failed', errorMsg);
        return { success: false, error: errorMsg };
      }

      // Clear temp session
      localStorage.removeItem('tempSession');

      // Set user data after successful password change
      if (data.tokens?.accessToken && data.user) {
        console.log('Password changed successfully for user:', data.user);
        
        // Use accessToken as specified by backend team
        const tokenToUse = data.tokens.accessToken;
        console.log('Using accessToken for API calls');
        
        setToken(tokenToUse);
        setUser(data.user);
        
        localStorage.setItem('authToken', tokenToUse);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        localStorage.setItem('allTokens', JSON.stringify(data.tokens));
        
        // Determine redirect path based on user role
        const redirectTo = data.user.role === 'admin' ? '/admin' : data.user.role === 'doctor' ? '/doctor' : '/';
        
        toast.success('Password changed', 'Your password has been updated successfully');
        return { success: true, redirectTo };
      } else if (data.token && data.user) {
        // Legacy format support
        console.log('Password changed successfully for user (legacy format):', data.user);
        setToken(data.token);
        setUser(data.user);
        
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        
        // Determine redirect path based on user role
        const redirectTo = data.user.role === 'admin' ? '/admin' : data.user.role === 'doctor' ? '/doctor' : '/';
        
        toast.success('Password changed', 'Your password has been updated successfully');
        return { success: true, redirectTo };
      }

      toast.success('Password changed', 'Your password has been updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      let errorMsg = 'Network error. Please try again.';
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMsg = 'Network error. Please check your connection and try again.';
      }
      toast.error('Password change failed', errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Refresh the access token using the refresh token
  const refreshTokenFn = async (): Promise<string | null> => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    
    if (!storedRefreshToken) {
      console.log('No refresh token available');
      return null;
    }

    try {
      console.log('🔄 Refreshing access token...');
      
      const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        },
        body: JSON.stringify({
          "AuthFlow": "REFRESH_TOKEN_AUTH",
          "ClientId": COGNITO_CLIENT_ID,
          "AuthParameters": {
            "REFRESH_TOKEN": storedRefreshToken
          }
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Token refresh failed:', data);
        // Refresh token might be expired - force re-login
        logout();
        return null;
      }

      const newAccessToken = data.AuthenticationResult?.AccessToken;
      const newIdToken = data.AuthenticationResult?.IdToken;
      
      if (newAccessToken) {
        console.log('✅ Token refreshed successfully');
        
        // Update stored tokens
        localStorage.setItem('authToken', newAccessToken);
        if (newIdToken) {
          localStorage.setItem('idToken', newIdToken);
        }
        
        setToken(newAccessToken);
        return newAccessToken;
      }
      
      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      return null;
    }
  };

  // Get a valid token, refreshing if necessary
  const getValidToken = async (): Promise<string | null> => {
    const currentToken = localStorage.getItem('authToken');
    
    if (!currentToken) {
      return null;
    }

    // Check if token is expired or about to expire
    if (isTokenExpired(currentToken)) {
      console.log('🔄 Token expired or expiring soon, refreshing...');
      return await refreshTokenFn();
    }

    return currentToken;
  };

  const logout = () => {
    console.log('🚪 Logging out user...');
    
    toast.info('Logged out', 'You have been logged out successfully');
    
    // Clear React state
    setUser(null);
    setToken(null);
    
    // Clear ALL localStorage - ensures no stale data from previous user
    localStorage.clear();
    
    // Clear ALL sessionStorage
    sessionStorage.clear();
    
    console.log('✅ Logout complete - all storage cleared');
    
    // Force redirect to login page with full page reload
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    logout,
    changePassword,
    refreshToken: refreshTokenFn,
    getValidToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};